import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Receipt, DollarSign, CheckCircle, CreditCard, Loader2 } from 'lucide-react';

function getInvoiceStatusBadge(status) {
  const styles = {
    pending: 'bg-warning/10 text-warning border-warning/20',
    paid: 'bg-success/10 text-success border-success/20',
    overdue: 'bg-error/10 text-error border-error/20',
    cancelled: 'bg-muted text-muted-foreground border-muted'
  };
  return <Badge variant="outline" className={styles[status] || styles.pending}>{status}</Badge>;
}

export function ClientInvoicesTab({ invoices, invoiceTotals, processingPayment, onPay }) {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Receipt className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{invoices.length}</p>
                <p className="text-sm text-muted-foreground">Total Invoices</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-warning/10 rounded-lg">
                <DollarSign className="w-6 h-6 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold">${invoiceTotals.total_due.toFixed(2)}</p>
                <p className="text-sm text-muted-foreground">Amount Due</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-success/10 rounded-lg">
                <CheckCircle className="w-6 h-6 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold">${invoiceTotals.total_paid.toFixed(2)}</p>
                <p className="text-sm text-muted-foreground">Total Paid</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoice List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary" />
            Your Invoices
          </CardTitle>
          <CardDescription>View and pay your invoices online</CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No invoices yet</p>
              <p className="text-sm">When your case manager creates invoices, they will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {invoices.map(invoice => (
                <div 
                  key={invoice.id}
                  className={`p-4 rounded-lg border ${invoice.status === 'overdue' ? 'border-error/30 bg-error/5' : 'border-border'}`}
                  data-testid={`invoice-${invoice.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono font-semibold">{invoice.invoice_number}</span>
                        {getInvoiceStatusBadge(invoice.status)}
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>Due: {new Date(invoice.due_date).toLocaleDateString()}</p>
                        {invoice.notes && <p className="text-xs">{invoice.notes}</p>}
                      </div>
                      <div className="mt-3 space-y-1">
                        {invoice.line_items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>{item.description} x {item.quantity}</span>
                            <span>${item.amount?.toFixed(2)}</span>
                          </div>
                        ))}
                        {invoice.tax_amount > 0 && (
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>Tax ({invoice.tax_rate}%)</span>
                            <span>${invoice.tax_amount?.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-6 text-right">
                      <p className="text-2xl font-bold">${invoice.total?.toFixed(2)}</p>
                      {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                        <Button 
                          className="mt-2 bg-primary text-white"
                          onClick={() => onPay(invoice.id)}
                          disabled={processingPayment}
                          data-testid={`pay-invoice-${invoice.id}`}
                        >
                          {processingPayment ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CreditCard className="w-4 h-4 mr-2" />
                          )}
                          Pay Now
                        </Button>
                      )}
                      {invoice.status === 'paid' && (
                        <p className="text-sm text-success mt-2 flex items-center justify-end gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Paid {invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString() : ''}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
